import { Injectable } from '@angular/core';
// models
import { Alternative } from '../models/alternative';
import { Answer } from '../models/answer';
import { Question } from '../models/question';
import { MatrixQuestion } from '../models/question-matrix';
import { Survey } from '../models/survey';
// services
import { TranslateService } from './translate.service';

@Injectable({
  providedIn: 'root'
})
export class QuestionFunctionalityService {

  default_lang = "";
  
  private readonly TTL = 5 * 60 * 1000; // 5 min TTL for caches
  private copiedAlternativesCache = new Map<string, { signature: string; ttl: number }>();
  private prepareDataCache = new Map<string, { data: any; ttl: number }>();

  constructor(private trans : TranslateService) {
    this.default_lang = this.trans.actualLang;
  }
  /**
   * logic to show copied alternatives from a previus question
   */
  copyAlternativesFromPreviusQuestion(question: Question, survey_data: Survey, answers: Array<Answer>) {
    let [copied_question_answers, has_answer] = this.prepareDataForCopiedQuestions(question, survey_data, answers);
    const answerSignature = this.buildAnswerSignature(copied_question_answers);
    const cacheKey = `copy_alt_${question.id}_${survey_data.id}`;
    const cached = this.copiedAlternativesCache.get(cacheKey);
    const requiresRecalc = question.alternatives?.some((a: Alternative) => a.copied && a.hidden === undefined) ?? false;
    if (cached && cached.signature === answerSignature && Date.now() < cached.ttl && !requiresRecalc) return;

    if (question.copy_alternatives_condition == 1) { // condition for checked answers
      // hide all copied alternatives, because they are expected to be marked in the parent question, to be shown here
      question.alternatives.filter(a => a.copied).forEach(a => a.hidden = true);

      if (has_answer) { // has answers?
        if (copied_question_answers.type == 3) { // multiple selection
          // console.log("multiple selection");
          // console.log("copied_question_answers.type == 3", copied_question_answers.type == 3);
          copied_question_answers.answer.forEach((alt: Alternative) => {
            const alternative_in_current_question = question.alternatives.filter(a => a.copied).find((a: Alternative) => {
              if (a.carry_forward_alternative_id == alt.id) return true;
            })
            if (alternative_in_current_question != undefined) alternative_in_current_question.hidden = false;
          });
        } else if (copied_question_answers.type == 4 || copied_question_answers.type == 7) { // single selection
          const selectedId = copied_question_answers.answer?.id;
          if (selectedId != null) {
            question.alternatives.filter(a => a.copied).find((a: Alternative) => {
              if (a.carry_forward_alternative_id == selectedId) a.hidden = false;
            });
          }
        }
      } else {

      }

    } else { // condition for unchecked answers

      // show all the copied alternatives, because it must be verified with the answers, the alternatives that were not selected 
      question.alternatives.filter(a => a.copied).forEach(a => a.hidden = false);

      if (has_answer) {
        if (copied_question_answers.type == 3) { // multiple selection
          copied_question_answers.answer.forEach((alt: Alternative) => {
            const alternative_in_current_question = question.alternatives.filter(a => a.copied).find((a: Alternative) => {
              if (a.carry_forward_alternative_id == alt.id) return true;
            })
            if (alternative_in_current_question != undefined) alternative_in_current_question.hidden = true;
          });

        } else if (copied_question_answers.type == 4 || copied_question_answers.type == 7) { // single selection
          const selectedId = copied_question_answers.answer?.id;
          if (selectedId != null) {
            question.alternatives.filter(a => a.copied).find((a: Alternative) => {
              if (a.carry_forward_alternative_id == selectedId) a.hidden = true;
            });
          }
        }
      }

    }

    // Cache the result (just mark that we've processed this)
    this.copiedAlternativesCache.set(cacheKey, { signature: answerSignature, ttl: Date.now() + this.TTL });
  }
  /**
 * logic to show copied alternatives from a previus question for scale matrix questions
 */
  copyAlternativesFromPreviusQuestionMatrixScale(question: Question, survey_data: Survey, answers: Array<Answer>) {
    let [copied_question_answers, has_answer] = this.prepareDataForCopiedQuestions(question, survey_data, answers);
    const answerSignature = this.buildAnswerSignature(copied_question_answers);
    const cacheKey = `copy_matrix_${question.id}_${survey_data.id}`;
    const cached = this.copiedAlternativesCache.get(cacheKey);
    const requiresRecalc = question.matrix_questions?.some((mq: MatrixQuestion) => mq.copied && mq.hidden === undefined) ?? false;
    if (cached && cached.signature === answerSignature && Date.now() < cached.ttl && !requiresRecalc) return;

    if (question.copy_alternatives_condition == 1) { // condition for checked answers
      // hide all copied alternatives, because they are expected to be marked in the parent question, to be shown here
      question.matrix_questions.filter(a => a.copied).forEach(a => a.hidden = true);

      if (has_answer) { // has answers?
        if (copied_question_answers.type == 3) { // multiple selection
          // console.log("multiple selection");
          // console.log("copied_question_answers.type == 3", copied_question_answers.type == 3);
          copied_question_answers.answer.forEach((alt: Alternative) => {
            
            const alternative_in_current_question = question.matrix_questions.filter((mq: MatrixQuestion) => mq.copied).find((mq: MatrixQuestion) => {
              return mq.carry_forward_alternative_id == alt.id;
            })
            if (alternative_in_current_question != undefined) alternative_in_current_question.hidden = false;
          });
        } else if (copied_question_answers.type == 4 || copied_question_answers.type == 7) { // single selection

          question.matrix_questions.filter(a => a.copied).find((mq: MatrixQuestion) => {
            if (mq.carry_forward_alternative_id == copied_question_answers.answer.id) mq.hidden = false;
          });
        }
      }

    } else { // condition for unchecked answers

      // show all the copied alternatives, because it must be verified with the answers, the alternatives that were not selected 
      question.matrix_questions.filter(a => a.copied).forEach(a => a.hidden = false);
      if (has_answer) {
        if (copied_question_answers.type == 3) { // multiple selection
          copied_question_answers.answer.forEach((alt: Alternative) => {
            const alternative_in_current_question = question.matrix_questions.filter((mq: MatrixQuestion) => mq.copied).find((mq: MatrixQuestion) => {
              return mq.carry_forward_alternative_id == alt.id;
            })
            if (alternative_in_current_question != undefined) alternative_in_current_question.hidden = true;
          });

        } else if (copied_question_answers.type == 4 || copied_question_answers.type == 7) { // single selection
          question.matrix_questions.filter(a => a.copied).find((mq: MatrixQuestion) => {
            if (mq.carry_forward_alternative_id == copied_question_answers.answer.id) mq.hidden = true;
          });
        }
      }

    }

    // Cache the result
    this.copiedAlternativesCache.set(cacheKey, { signature: answerSignature, ttl: Date.now() + this.TTL });
  }
  /**
   * 
   * @param question 
   * @returns answers of the question which the alternatives were copied and if it has answers or not 
   */
  prepareDataForCopiedQuestions(question: Question, survey_data: Survey, answers: Array<Answer>): [copied_question_answers: any, has_answer: boolean] {
    let copied_question: Question;
    survey_data.pages.every(p => {
      copied_question = p.questions.find(q => q.id == question.copy_alternatives_question_id) as Question;
      return copied_question == undefined;
    })

    // answers ahora es parámetro
    const copied_question_answers = answers.find((a: Answer) => a.question_id == copied_question.id);
    const answerSignature = this.buildAnswerSignature(copied_question_answers);
    const cacheKey = `prepare_${question.id}_${survey_data.id}_${copied_question?.id}_${answerSignature}`;
    const cached = this.prepareDataCache.get(cacheKey);
    if (cached && Date.now() < cached.ttl) return cached.data;

    let has_answer = copied_question_answers != undefined;
    if (has_answer) {
      if (copied_question_answers.type == 3) {
        has_answer = copied_question_answers.answer?.length > 0;
      } else if (copied_question_answers.type == 4 || copied_question_answers.type == 7) {
        has_answer = copied_question_answers.answer != null;
      }
    }
    
    // filter copied alternatives
    if (question.type != 100) { question.alternatives.forEach((a: Alternative) => a.copied = a.carry_forward_alternative_id != null);
    } else {
      question.matrix_questions.forEach((a: MatrixQuestion) => {
        a.copied = a.carry_forward_alternative_id != null;
      })
    }

    const result = [copied_question_answers, has_answer] as [any, boolean];
    this.prepareDataCache.set(cacheKey, { data: result, ttl: Date.now() + this.TTL });
    return result;
  }

  private buildAnswerSignature(copied_question_answers: Answer | undefined): string {
    if (!copied_question_answers) return "none";
    const answer = copied_question_answers.answer;
    if (answer == null) return "none";
    if (Array.isArray(answer)) {
      const ids = answer
        .map((alt: Alternative) => alt?.id)
        .filter((id: number | undefined) => id != undefined)
        .sort((a: number, b: number) => a - b);
      return `array:${ids.join(",")}`;
    }
    if (answer && typeof answer === "object" && "id" in answer) {
      return `id:${(answer as Alternative).id}`;
    }
    return `value:${String(answer)}`;
  }
  /**
   * Convert base64 to file
   * @param dataurl 
   * @param filename 
   * @returns file
   */
  dataURLtoFile(dataurl: string, filename: string) {

    var arr = dataurl.split(','),
      mime = arr[0].match(/:(.*?);/)[1],
      bstr = atob(arr[1]),
      n = bstr.length,
      u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  }

  /**
   * Clear caches when survey data changes
   */
  clearCaches(): void {
    this.copiedAlternativesCache.clear();
    this.prepareDataCache.clear();
  }
}
